import React from 'react';
import { formatSessionTimestamp } from '../../utils/sessionHistory';

const PREVIOUS_SESSION_SCROLL_LIMIT = 8;

/**
 * "My Sessions" list.
 *
 * Renders the metadata-only index from `users/{uid}/sessions`; event documents
 * are never copied into the profile.
 */
export default function SessionList({
  title,
  sessions,
  emptyMessage,
  onOpen,
  busyEventId = null,
  collapsible = false,
  defaultCollapsed = true,
}) {
  const [expanded, setExpanded] = React.useState(!collapsible || !defaultCollapsed);
  const bounded = collapsible && sessions.length > PREVIOUS_SESSION_SCROLL_LIMIT;
  const heading = collapsible ? `${title} (${sessions.length})` : title;

  const list = (
    sessions.length === 0 ? (
      <p className="text-sm text-gray-500">{emptyMessage}</p>
    ) : (
      <ul className={bounded ? 'space-y-2 max-h-72 overflow-y-auto pr-1' : 'space-y-2'}>
        {sessions.map((session) => (
          <li
            key={session.eventId}
            className="rhythm-session-card flex items-center gap-3 rounded-lg bg-white/5"
          >
            <div className="min-w-0 flex-1">
              <p className="rhythm-session-card__title truncate font-medium text-white">
                {session.title || session.eventId}
              </p>
              <p className="text-xs text-gray-400">
                <span className="font-mono">{session.eventId}</span>
                {' · '}
                <span className={session.role === 'host' ? 'text-red-400' : 'text-purple-300'}>
                  {session.role === 'host' ? 'Host' : 'Guest'}
                </span>
                {formatSessionTimestamp(session) && ` · ${formatSessionTimestamp(session)}`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onOpen?.(session)}
              disabled={busyEventId === session.eventId}
              className="shrink-0 rounded-lg bg-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/20 transition-colors disabled:opacity-60"
            >
              {busyEventId === session.eventId ? 'Opening…' : 'Open'}
            </button>
          </li>
        ))}
      </ul>
    )
  );

  if (!collapsible) {
    return (
      <section className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-400">{heading}</h3>
        {list}
      </section>
    );
  }

  return (
    <section className="space-y-2">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 rounded-lg py-1 text-left"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
      >
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-400">{heading}</h3>
        <span aria-hidden="true" className="text-gray-400">{expanded ? '▴' : '▾'}</span>
      </button>
      {expanded ? list : null}
    </section>
  );
}
